import { Box, Button, Chip, Stack, Switch, Typography, useMediaQuery } from '@mui/material'
import { FormTextInputDisplay } from '../../form-field-components/new-and-edit/form-text-input'
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { FormColorInputDisplay } from '../../form-field-components/new-and-edit/form-color-input'
import { UserDetailsContext } from '../../../../context/user-details/user-details'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels'
import type { IProjectReportSetting } from '../../../../shared/services/project-report-setting/project-report-setting.model'
import ProjectReportSettingService from '../../../../shared/services/project-report-setting/project-report-setting.service'
import { FormImageInputDisplay } from '../../form-field-components/new-and-edit/form-image-input'
import { GaiaWebPageCoverImageDialog } from '../../../dialogs/reporting/gaia-web-page-cover-image-dialog'
import gaiaTheme from '../../../../root-components/theme'
import {
  GAIA_REPORT_CHART_SERIES_COUNT,
  buildGaiaReportColorPalette,
  getReportChartSeriesColors,
} from '../../../../modules/public-reports/utils/gaia-report-color-palette'
import { getMixedColorPalette } from '../../../../shared/utils/colors-utils'

export const ReportingProjectWebPageSettingsForm: FunctionComponent<{
  settings: IProjectReportSetting
  setSettings: React.Dispatch<React.SetStateAction<any>>
  projectId: string
}> = ({ settings, setSettings, projectId }) => {
  const settingService = new ProjectReportSettingService()
  const userDetailsContext = useContext(UserDetailsContext)
  const userNotificationsContext = useContext(UserNotificationsContext)
  const screenLabelsContext = useContext(ScreenLabelsContext)
  const userDetails = userDetailsContext.userDetails
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'))

  const [coverImageFileData, setCoverImageFileData] = useState([])
  const [showDefaultImages, setShowDefaultImages] = useState(false)
  const [editField, setEditField] = useState('')
  const [localThemeColor, setLocalThemeColor] = useState(settings?.themeColor || '')
  const [localTextColor, setLocalTextColor] = useState(settings?.textColor || '')
  const [localCoverImage, setLocalCoverImage] = useState(settings?.coverImageFilepath || '')
  const [localPaletteOverrides, setLocalPaletteOverrides] = useState(() => ({
    light: settings?.paletteOverrides?.light ?? '',
    dark: settings?.paletteOverrides?.dark ?? '',
    veryLight: settings?.paletteOverrides?.veryLight ?? '',
    veryDark: settings?.paletteOverrides?.veryDark ?? '',
  }))
  const [localBarChartColors, setLocalBarChartColors] = useState<string[]>(() =>
    Array.from({ length: GAIA_REPORT_CHART_SERIES_COUNT }, (_, i) =>
      typeof settings?.paletteOverrides?.barChartColors?.[i] === 'string'
        ? (settings?.paletteOverrides?.barChartColors?.[i] as string)
        : ''
    )
  )
  const themeBaseColor = settings?.themeColor || userDetails?.organisation?.primaryColor || '#70942c'
  const effectivePalette = buildGaiaReportColorPalette(themeBaseColor, settings?.paletteOverrides ?? null)
  const effectiveChartSeries = getReportChartSeriesColors(themeBaseColor, settings?.paletteOverrides ?? null)

  useEffect(() => {
    setLocalPaletteOverrides({
      light: settings?.paletteOverrides?.light ?? '',
      dark: settings?.paletteOverrides?.dark ?? '',
      veryLight: settings?.paletteOverrides?.veryLight ?? '',
      veryDark: settings?.paletteOverrides?.veryDark ?? '',
    })
    setLocalBarChartColors(
      Array.from({ length: GAIA_REPORT_CHART_SERIES_COUNT }, (_, i) => {
        const v = settings?.paletteOverrides?.barChartColors?.[i]
        return typeof v === 'string' ? v : ''
      })
    )
  }, [settings])

  const mergePaletteOverride = (key: 'light' | 'dark' | 'veryLight' | 'veryDark', raw: string) => {
    const prev = settings?.paletteOverrides || {}
    const next: Record<string, unknown> = { ...prev }
    const v = raw.trim()
    if (!v || !/^#[0-9A-Fa-f]{6}$/i.test(v)) { delete next[key] } else { next[key] = v.toLowerCase() }
    return Object.keys(next).length ? next : null
  }

  const mergeBarChartSeriesOverrides = () => {
    const prev = settings?.paletteOverrides || {}
    const auto = getMixedColorPalette(themeBaseColor)
    const barChartColors = localBarChartColors.map((s, i) => {
      const t = s.trim()
      if (!t || !/^#[0-9A-Fa-f]{6}$/i.test(t)) return null
      return t.toLowerCase() === auto[i].toLowerCase() ? null : t.toLowerCase()
    })
    const hasBar = barChartColors.some(x => x != null)
    const next: Record<string, unknown> = { ...prev }
    if (hasBar) next.barChartColors = barChartColors
    else delete next.barChartColors
    return Object.keys(next).length ? next : null
  }

  const submitHandler = async (fieldName?: string, value?: any) => {
    const field = fieldName || editField
    if (!field || value === undefined || !projectId) return
    const updated = { ...settings, [field]: value }
    try {
      const saved = await settingService.updateSettings(projectId, updated)
      setSettings(saved)
      setEditField('')
      userNotificationsContext.openSnackbar({ open: true, message: 'Updated Successfully' })
      return saved
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
      return null
    }
  }

  const uploadCoverImageHandler = async () => {
    if (!projectId || coverImageFileData.length === 0) return
    const form = new FormData()
    form.append('files', coverImageFileData[0])
    form.append('organisation', JSON.stringify({ organisationId: userDetails?.organisation.id }))
    try {
      const saved = await settingService.saveCoverImage(projectId, form)
      setCoverImageFileData([])
      setSettings(saved)
      setLocalCoverImage(saved?.coverImageFilepath || '')
      setEditField('')
      userNotificationsContext.openSnackbar({ open: true, message: 'Updated Successfully' })
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
    }
  }

  const selectDefaultImage = async (imageUrl: string) => {
    setShowDefaultImages(false)
    const saved = await submitHandler('coverImageFilepath', imageUrl)
    if (saved) setLocalCoverImage(saved.coverImageFilepath || '')
  }

  const removeCoverImage = async () => {
    setLocalCoverImage('')
    const saved = await submitHandler('coverImageFilepath', null)
    if (saved) setLocalCoverImage(saved.coverImageFilepath || '')
  }

  const cancelEditHandler = () => {
    setEditField('')
    setLocalThemeColor(settings?.themeColor || '')
    setLocalTextColor(settings?.textColor || '')
    setLocalCoverImage(settings?.coverImageFilepath || '')
    setLocalPaletteOverrides({
      light: settings?.paletteOverrides?.light ?? '',
      dark: settings?.paletteOverrides?.dark ?? '',
      veryLight: settings?.paletteOverrides?.veryLight ?? '',
      veryDark: settings?.paletteOverrides?.veryDark ?? '',
    })
    setLocalBarChartColors(
      Array.from({ length: GAIA_REPORT_CHART_SERIES_COUNT }, (_, i) => {
        const v = settings?.paletteOverrides?.barChartColors?.[i]
        return typeof v === 'string' ? v : ''
      })
    )
  }

  const resetDerivedColoursToThemeDefaults = async () => {
    if (!projectId) return
    try {
      const saved = await settingService.updateSettings(projectId, { ...settings, paletteOverrides: null })
      setSettings(saved)
      setLocalPaletteOverrides({ light: '', dark: '', veryLight: '', veryDark: '' })
      setLocalBarChartColors(Array.from({ length: GAIA_REPORT_CHART_SERIES_COUNT }, () => ''))
      setEditField('')
      userNotificationsContext.openSnackbar({ open: true, message: 'Updated Successfully' })
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
    }
  }

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editField === 'themeColor') submitHandler('themeColor', localThemeColor)
    else if (editField === 'primaryColor') submitHandler('textColor', localTextColor)
    else if (editField === 'paletteLight') submitHandler('paletteOverrides', mergePaletteOverride('light', localPaletteOverrides.light))
    else if (editField === 'paletteDark') submitHandler('paletteOverrides', mergePaletteOverride('dark', localPaletteOverrides.dark))
    else if (editField === 'paletteVeryLight') submitHandler('paletteOverrides', mergePaletteOverride('veryLight', localPaletteOverrides.veryLight))
    else if (editField === 'paletteVeryDark') submitHandler('paletteOverrides', mergePaletteOverride('veryDark', localPaletteOverrides.veryDark))
    else if (editField?.startsWith('barChart')) submitHandler('paletteOverrides', mergeBarChartSeriesOverrides())
    else if (editField === 'coverImage') submitHandler('coverImageFilepath', localCoverImage)
  }

  const handleColorThemeChange = (val: string | React.ChangeEvent<HTMLInputElement>) =>
    setLocalThemeColor(typeof val === 'string' ? val : val.target.value)

  const handleColorTextChange = (val: string | React.ChangeEvent<HTMLInputElement>) =>
    setLocalTextColor(typeof val === 'string' ? val : val.target.value)

  const handlePaletteInputChange = (key: 'light' | 'dark' | 'veryLight' | 'veryDark') =>
    (val: string | React.ChangeEvent<HTMLInputElement>) => {
      const s = typeof val === 'string' ? val : val.target.value
      setLocalPaletteOverrides(prev => ({ ...prev, [key]: s }))
    }

  const handleBarChartInputChange = (index: number) =>
    (val: string | React.ChangeEvent<HTMLInputElement>) => {
      const s = typeof val === 'string' ? val : val.target.value
      setLocalBarChartColors(prev => { const next = [...prev]; next[index] = s; return next })
    }

  const sectionTitleSx = { fontWeight: 700, color: 'text.primary', pl: 3, pb: 1.5 } as const

  const publicUrl = `${window.location.origin}/public/project-report/${projectId}`

  return (
    <Box sx={{ width: '100%' }}>
      <Box component='form' onSubmit={handleFormSubmit}>
        <Typography variant='subtitle1' sx={{ ...sectionTitleSx, pt: 0 }}>
          Report layout
        </Typography>
        <FormImageInputDisplay
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          disabled={editField !== 'coverImage'}
          isTopRow={true}
          regularFieldLabel
          title='Cover image'
          name='coverImage'
          value={editField === 'coverImage' ? localCoverImage : (settings?.coverImageFilepath ?? undefined)}
          onChange={() => { }}
          onImageUpload={uploadCoverImageHandler}
          onImageRemove={removeCoverImage}
          onImageLibrarySelect={() => setShowDefaultImages(true)}
          showImageLibraryButton={true}
          cancelEditHandler={cancelEditHandler}
          fileData={coverImageFileData}
          setFileData={setCoverImageFileData}
          onFileUploadSubmit={uploadCoverImageHandler}
          disableUploadButton={!userDetails?.organisation?.name}
          hideSaveButton={true}
          isBottomRow={true}
        />

        <Typography variant='subtitle1' sx={{ ...sectionTitleSx, pt: 4 }}>
          Public link
        </Typography>
        <Stack sx={{
          border: '1px solid',
          borderColor: 'lightGrey.main',
          borderRadius: '10px',
          p: 2,
          pl: 3,
          mb: 2,
        }}>
          <Stack direction={isMobileMode ? 'column' : 'row'} alignItems={isMobileMode ? 'flex-start' : 'center'} justifyContent='space-between' gap={2}>
            <Stack>
              <Typography variant='subtitle1' sx={{ fontWeight: 400 }}>Make report public</Typography>
              <Typography variant='body2' color='text.secondary'>
                When enabled, anyone with the link can view this report without logging in.
              </Typography>
            </Stack>
            <Switch
              checked={!!settings?.isPublic}
              onChange={(e) => submitHandler('isPublic', e.target.checked)}
              color='primary'
            />
          </Stack>
          {settings?.isPublic && (
            <Stack direction='row' alignItems='center' gap={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
              <Chip label={publicUrl} variant='outlined' size='small' sx={{ fontFamily: 'monospace', maxWidth: '100%' }} />
              <Button
                size='small'
                variant='outlined'
                onClick={() => {
                  navigator.clipboard.writeText(publicUrl)
                  userNotificationsContext.openSnackbar({ open: true, message: 'Link copied to clipboard' })
                }}
              >
                Copy link
              </Button>
            </Stack>
          )}
        </Stack>

        <Typography variant='subtitle1' sx={{ ...sectionTitleSx, pt: 2 }}>
          Colours
        </Typography>
        <FormColorInputDisplay
          isTopRow={true}
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          disabled={editField !== 'themeColor'}
          title='Theme colour'
          name='themeColor'
          tooltip='Sets the defaults for the other colour fields below when they are not customised.'
          value={editField === 'themeColor' ? localThemeColor : (settings?.themeColor ?? undefined)}
          onChange={handleColorThemeChange}
          cancelEditHandler={cancelEditHandler}
          afterHexAction={
            <Button type='button' variant='text' size='small' onClick={resetDerivedColoursToThemeDefaults} sx={{ textTransform: 'none', fontWeight: 400 }}>
              Reset to theme defaults
            </Button>
          }
        />
        <FormColorInputDisplay
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          disabled={editField !== 'primaryColor'}
          title='Text colour'
          name='primaryColor'
          value={editField === 'primaryColor' ? localTextColor : (settings?.textColor ?? undefined)}
          onChange={handleColorTextChange}
          cancelEditHandler={cancelEditHandler}
        />
        <FormColorInputDisplay
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          disabled={editField !== 'paletteLight'}
          title='Primary colour (light)'
          name='paletteLight'
          value={editField === 'paletteLight' ? localPaletteOverrides.light || effectivePalette.light : effectivePalette.light}
          onChange={handlePaletteInputChange('light')}
          cancelEditHandler={cancelEditHandler}
        />
        <FormColorInputDisplay
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          disabled={editField !== 'paletteDark'}
          title='Primary colour (dark)'
          name='paletteDark'
          value={editField === 'paletteDark' ? localPaletteOverrides.dark || effectivePalette.dark : effectivePalette.dark}
          onChange={handlePaletteInputChange('dark')}
          cancelEditHandler={cancelEditHandler}
        />
        <FormColorInputDisplay
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          disabled={editField !== 'paletteVeryLight'}
          title='Background colour'
          name='paletteVeryLight'
          value={editField === 'paletteVeryLight' ? localPaletteOverrides.veryLight || effectivePalette.veryLight : effectivePalette.veryLight}
          onChange={handlePaletteInputChange('veryLight')}
          cancelEditHandler={cancelEditHandler}
        />
        <FormColorInputDisplay
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          disabled={editField !== 'paletteVeryDark'}
          title='Highlight colour'
          name='paletteVeryDark'
          bottomBorder
          isBottomRow
          value={editField === 'paletteVeryDark' ? localPaletteOverrides.veryDark || effectivePalette.veryDark : effectivePalette.veryDark}
          onChange={handlePaletteInputChange('veryDark')}
          cancelEditHandler={cancelEditHandler}
        />

        <Typography variant='subtitle1' sx={{ ...sectionTitleSx, pt: 4 }}>
          Chart colours
        </Typography>
        <Box sx={{ border: '1px solid', borderColor: 'lightGrey.main', borderRadius: '10px', overflow: 'hidden' }}>
          {Array.from({ length: GAIA_REPORT_CHART_SERIES_COUNT }, (_, i) => {
            const name = `barChart${i}`
            const isLast = i === GAIA_REPORT_CHART_SERIES_COUNT - 1
            return (
              <FormColorInputDisplay
                key={name}
                isEditPage={true}
                editField={editField}
                setEditField={setEditField}
                disabled={editField !== name}
                title={`Chart colour ${i + 1}`}
                name={name}
                value={editField === name ? localBarChartColors[i] || effectiveChartSeries[i] : effectiveChartSeries[i]}
                onChange={handleBarChartInputChange(i)}
                cancelEditHandler={cancelEditHandler}
                sideBorders={false}
                topBorder={i !== 0}
                bottomBorder={!isLast}
              />
            )
          })}
        </Box>
      </Box>

      {showDefaultImages && (
        <GaiaWebPageCoverImageDialog
          open={showDefaultImages}
          selectDefaultImage={selectDefaultImage}
          setShowDefaultImages={setShowDefaultImages}
        />
      )}
    </Box>
  )
}
